import React, { useState, useEffect } from 'react';
import { X, Upload, CheckCircle2 } from 'lucide-react';
import API from '../api/api';

// Bulk shipment upload from the Excel "Load Detail" sheet, opened from the
// dashboard toolbar. Rows whose Customer LD# already exists merge into that
// shipment instead of creating a duplicate (core/utils/bulk_upload.py).
function BulkUploadShipments({ isOpen, onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);   // { created, merged, mergedRows, warning }
  const [organizationId, setOrganizationId] = useState(null);
  const [loadingOrg, setLoadingOrg] = useState(true);

  useEffect(() => {
    if (!isOpen || organizationId) return;
    (async () => {
      try {
        const response = await API.get('shipments/bulk-upload/');
        setOrganizationId(response.data.organization_id);
      } catch (err) {
        console.error('Error fetching organization ID:', err);
        setError('Failed to fetch organization information');
      } finally {
        setLoadingOrg(false);
      }
    })();
  }, [isOpen, organizationId]);

  useEffect(() => {
    if (!isOpen) {
      setFile(null);
      setError(null);
      setResult(null);
    }
  }, [isOpen]);

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;
    const isExcel = /\.(xlsx|xls)$/i.test(selected.name)
      || selected.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      || selected.type === 'application/vnd.ms-excel';
    if (isExcel) {
      setFile(selected);
      setError(null);
      setResult(null);
    } else {
      setError('Please upload an Excel file (.xlsx or .xls)');
      setFile(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { setError('Please select a file to upload'); return; }
    if (!organizationId) { setError('Organization information not available. Please refresh the page.'); return; }

    setLoading(true);
    setError(null);
    setResult(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('organization_id', organizationId);

    try {
      const { data } = await API.post('shipments/bulk-upload/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // A file whose rows all merged into existing shipments creates nothing
      // and is still a success.
      const created = data.count || 0;
      const merged = data.merged_count || 0;
      const errorRows = data.error_rows || [];
      const errorSummary = errorRows.length
        ? `${errorRows.length} row(s) had errors (e.g. duplicate Pro#): `
          + errorRows.slice(0, 5).map((r) => `Pro# ${r.pro}`).join(', ')
          + (errorRows.length > 5 ? ` …and ${errorRows.length - 5} more` : '') + '.'
        : '';

      if (data.message && (created > 0 || merged > 0)) {
        setResult({
          created, merged,
          mergedRows: data.merged || [],
          warning: [data.warning, errorSummary].filter(Boolean).join(' ') || null,
        });
        setFile(null);
        e.target.reset();
        onUploaded?.();
      } else if (data.message) {
        const skipped = data.skipped_rows?.length || 0;
        setError(
          errorSummary || data.warning
          || `Upload processed but no shipments were created (${skipped} rows skipped). `
             + 'Check that required fields (Pro#, Customer LD#, PU Date, DEL Date) are present.'
        );
      } else {
        setError(data.error || 'Failed to upload shipments');
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.error || err.response?.data?.detail || 'Failed to upload shipments. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg my-8">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Bulk upload shipments</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Excel sheet in the Load Detail layout. Rows matching an existing Customer LD# update that shipment.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && <div className="rounded-md bg-red-50 p-3 text-sm text-red-800">{error}</div>}

          {result && (
            <div className="rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-800">
              <p className="font-medium flex items-center gap-1.5">
                <CheckCircle2 size={15} />
                Created {result.created} shipment{result.created === 1 ? '' : 's'}
                {result.merged > 0 && `, merged ${result.merged} into existing records`}.
              </p>
              {result.mergedRows.length > 0 && (
                <details className="mt-2 text-xs text-green-700">
                  <summary className="cursor-pointer">Show merged records</summary>
                  <ul className="mt-1 list-disc list-inside max-h-40 overflow-y-auto">
                    {result.mergedRows.map((row) => (
                      <li key={`${row.cust_po_number}-${row.pro_number}`}>
                        Customer LD# {row.cust_po_number} &rarr; Pro# {row.pro_number}
                        {row.changed_fields?.length ? ` (updated: ${row.changed_fields.join(', ')})` : ' (no changes)'}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              {result.warning && <p className="text-xs text-amber-700 mt-2">{result.warning}</p>}
            </div>
          )}

          <label htmlFor="bulk-upload-file"
                 className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
            <Upload size={24} className="text-gray-400 mb-2" />
            <p className="text-sm text-gray-500">
              {file ? <span className="font-medium text-gray-700">{file.name}</span>
                    : <><span className="font-semibold">Click to choose a file</span> (.xlsx, .xls)</>}
            </p>
            <input id="bulk-upload-file" type="file" className="hidden" accept=".xlsx,.xls" onChange={handleFileChange} />
          </label>

          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
              {result ? 'Done' : 'Cancel'}
            </button>
            <button type="submit" disabled={loading || !file || loadingOrg || !organizationId}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50">
              {loading ? 'Uploading…' : loadingOrg ? 'Loading…' : 'Upload'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default BulkUploadShipments;
